import React from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Title, Button, Container, Paper, Text, Box } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { logger } from '../../utils/logger';

/**
 * Creates a comprehensive error boundary with Mantine styling, each route can now
 * have it's own error boundary. Handles both route errors and unexpected/general errors.
 */
interface ErrorBoundaryProps {
  children?: React.ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    logger.error('Route error:', error.status, error.statusText, error.data);
    return (
      <Container size="md" mt="xl">
        <Paper shadow="md" p="xl" radius="md" ta="center">
          <Box mb="md">
            <IconAlertCircle
              size={64}
              style={{ color: 'var(--mantine-color-red-6)' }}
            />
          </Box>
          <Title
            order={1}
            style={{ color: 'var(--mantine-color-red-6)' }}
            mb="md"
          >
            {error.status} - {error.statusText}
          </Title>
          <Text style={{ color: 'var(--mantine-color-dimmed)' }} mb="lg">
            {error.data?.message || 'Something went wrong'}
          </Text>
          <Button variant="filled" onClick={() => (window.location.href = '/')}>
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  if (error instanceof Error) {
    logger.error('Error boundary caught:', error.message, error);
    return (
      <Container size="md" mt="xl">
        <Paper shadow="md" p="xl" radius="md" ta="center">
          <Box mb="md">
            <IconAlertCircle
              size={64}
              style={{ color: 'var(--mantine-color-red-6)' }}
            />
          </Box>
          <Title
            order={1}
            style={{ color: 'var(--mantine-color-red-6)' }}
            mb="md"
          >
            Unexpected Error
          </Title>
          <Text style={{ color: 'var(--mantine-color-dimmed)' }} mb="lg">
            {error.message || 'An unexpected error occurred'}
          </Text>
          <Button variant="filled" onClick={() => (window.location.href = '/')}>
            Go Home
          </Button>
        </Paper>
      </Container>
    );
  }

  // If no error, render children
  return <>{children}</>;
};

export default ErrorBoundary;
